import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { logEvent } from "@/hooks/use-event-log";
import { parseISODate } from "@/lib/format";
import type { DBUser } from "@/lib/db-types";

export interface TimeEntry {
  id: string;
  project_id: string | null;
  user_id: string | null;
  task_description: string;
  duration_seconds: number;
  start_date: string;
  budgeted_hours: number | null;
  manual_hours_override: number | null;
  status: string;
  comment: string | null;
  weekend_holiday_hours: number | null;
  overnight_count: number | null;
  overtime_comment: string | null;
  budget_line_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface TimeEntryInsert {
  project_id?: string | null;
  user_id?: string | null;
  task_description: string;
  duration_seconds: number;
  start_date: string;
  budgeted_hours?: number | null;
  manual_hours_override?: number | null;
  status?: string;
  comment?: string | null;
  weekend_holiday_hours?: number | null;
  overnight_count?: number | null;
  overtime_comment?: string | null;
  budget_line_id?: string | null;
  assignment_id?: string | null; // VIB-1144: koble manuell-føring til assignment
}

interface TimeEntryUpdate {
  project_id?: string | null;
  task_description?: string;
  duration_seconds?: number;
  start_date?: string;
  budgeted_hours?: number | null;
  manual_hours_override?: number | null;
  status?: string;
  comment?: string | null;
  weekend_holiday_hours?: number | null;
  overnight_count?: number | null;
  overtime_comment?: string | null;
  budget_line_id?: string | null;
  assignment_id?: string | null; // VIB-1144: koble manuell-føring til assignment
}

/**
 * A4-smoke-fiks (2026-07-18): PostgREST tolker en dato-only-streng
 * ("YYYY-MM-DD") mot en `timestamptz`-kolonne som UTC-midnatt. En inklusiv
 * `.lte("start_date", endDate)`-øvre-grense ekskluderer derfor stille alle
 * føringer med et EKTE klokkeslett PÅ selve `endDate` (Oslo er UTC+1/+2, så
 * ethvert klokkeslett etter midnatt UTC havner over grensen) — konkret:
 * ukesoversiktens siste dag (søndag) forsvant fra "Timer"-kortet på Mine
 * oppgaver. `DayGridView` støtte på samme rot og måtte kompensere manuelt ved
 * kallesiden (send dagen ETTER som "endDate" + filtrer med `isSameDay`);
 * denne fiksen løser den i selve hooken slik at ALLE kallere (uke-/
 * månedsvisning i `MonthlyEntriesCard` inkludert) får riktig oppførsel uten
 * workaround.
 */
function exclusiveUpperBound(endDate: string): string {
  return format(addDays(parseISODate(endDate), 1), "yyyy-MM-dd");
}

/**
 * Fetch time entries for a specific user within a date range (inclusive of
 * both `startDate` and `endDate` calendar days).
 */
async function fetchTimeEntries(
  userId: string,
  startDate: string,
  endDate: string
): Promise<TimeEntry[]> {
  const { data, error } = await supabase
    .from("time_entries")
    .select("*")
    .eq("user_id", userId)
    .gte("start_date", startDate)
    .lt("start_date", exclusiveUpperBound(endDate))
    .is("deleted_at", null)
    .order("start_date", { ascending: true });

  if (error) throw error;
  return (data as TimeEntry[]) || [];
}

export function useTimeEntries(
  userId: string | null,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: ["time-entries", userId, startDate, endDate],
    queryFn: () => {
      if (!userId) throw new Error("User ID is required");
      return fetchTimeEntries(userId, startDate, endDate);
    },
    enabled: !!userId,
  });
}

/**
 * Fetch time entries for a specific budget line (task)
 */
export function useTaskTimeEntries(
  userId: string | null,
  budgetLineId: string | null
) {
  return useQuery({
    queryKey: ["task-time-entries", userId, budgetLineId],
    queryFn: async (): Promise<TimeEntry[]> => {
      if (!userId || !budgetLineId) return [];
      const { data, error } = await supabase
        .from("time_entries")
        .select("*")
        .eq("user_id", userId)
        .eq("budget_line_id", budgetLineId)
        .is("deleted_at", null)
        .order("start_date", { ascending: false });
      if (error) throw error;
      return (data as TimeEntry[]) ?? [];
    },
    enabled: !!userId && !!budgetLineId,
  });
}

/**
 * Create a new time entry
 */
async function createTimeEntry(entry: TimeEntryInsert): Promise<TimeEntry> {
  const { data, error } = await supabase
    .from("time_entries")
    .insert([entry])
    .select()
    .single();

  if (error) throw error;
  return data as TimeEntry;
}

export function useCreateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTimeEntry,
    onSuccess: async (entry) => {
      await invalidateFor(queryClient, "time_entries", {
        userId: entry.user_id ?? undefined,
        projectId: entry.project_id ?? undefined,
      });
      toast.success("Timeregistrering opprettet");
      // DB-triggeren log_time_entry_inserted skriver time_entry_created til
      // project_events (per-prosjekt-loggen). Den globale event_log fanges IKKE
      // av triggeren, så vi logger dit her — ellers vises kun stoppeklokke-timer
      // (TimerContext) i Hendelsesloggen, ikke manuell registrering. Samme
      // payload-form som triggeren/registry-formatteren ({ hours, ... }).
      if (entry.project_id) {
        void logEvent({
          event_type: "time_entry_created",
          entity_type: "project",
          entity_id: entry.project_id,
          project_id: entry.project_id,
          user_id: entry.user_id ?? undefined,
          payload: {
            hours: Math.round(((entry.duration_seconds ?? 0) / 3600) * 100) / 100,
            task_description: entry.task_description ?? "",
            duration_seconds: entry.duration_seconds ?? 0,
          },
        });
      }
    },
    onError: (err) => {
      const msg = (err as Error)?.message ?? "";
      // DB-triggeren assert_internal_time_entry_has_comment raiser «...krever
      // kommentar» — vis tydelig melding i stedet for generisk feil.
      toast.error(
        msg.includes("krever kommentar")
          ? "Kommentar er påkrevd for interne prosjekter."
          : "Noe gikk galt",
      );
    },
  });
}

/**
 * Update an existing time entry
 */
async function updateTimeEntry(
  entryId: string,
  updates: TimeEntryUpdate
): Promise<TimeEntry> {
  const { data, error } = await supabase
    .from("time_entries")
    .update(updates)
    .eq("id", entryId)
    .select()
    .single();

  if (error) throw error;
  return data as TimeEntry;
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, updates }: { entryId: string; updates: TimeEntryUpdate }) =>
      updateTimeEntry(entryId, updates),
    onSuccess: async () => {
      // Vi har ikke userId/projectId fra updateTimeEntry-respons her, så
      // bredt prefix-invalidering (faller tilbake til ["time-entries"] osv.)
      await invalidateFor(queryClient, "time_entries");
      toast.success("Timeregistrering oppdatert");
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

/**
 * Soft delete a time entry
 */
async function deleteTimeEntry(entryId: string): Promise<void> {
  const { error } = await supabase
    .from("time_entries")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", entryId);

  if (error) throw error;
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTimeEntry,
    onSuccess: async () => {
      // Vi har ikke userId fra entryId-argumentet. Bredt prefix.
      await invalidateFor(queryClient, "time_entries");
      toast.success("Timeregistrering slettet");
      // VIB-1184 D15: time_entries.deleted (med snapshot) logges av DB-triggeren
      // snapshot_row_on_delete (event_log). Frontend-duppen fjernet.
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}
